import { cardImageSrc, CARD_RATIO, isLegacyArt } from "@/lib/artAssets";
import { cardAlt } from "@/components/game/CardArt";

const W = 280;
const H = Math.round(W * CARD_RATIO);

// Card detail slot: always reserved at 280x448 (stage px, 900x1440 card frame), top of the 504-tall card row.
// width: smaller slot on the tablet layout (252x403); the card keeps its 900x1440 proportions.
// Empty until a hand or market card (or a board square) is clicked. Never dimmed; swapping cards / clearing fades in over 150ms.
export const SLOT_W = W;
export const SLOT_H = H;
// detail: a line (or a list of lines) drawn over the bottom of the card (board square read: e.g. the
// square's Sea Wall lines and its height line).
// emptyHeight: the empty "Select a card" box may stretch to this height (down to the board bottom).
export default function LargeCardSlot({ card, source, onOpen, width = W, note = null, detail = null, emptyHeight = null }) {
  const detailLines = (Array.isArray(detail) ? detail : [detail]).filter(Boolean);
  const w = width;
  const h = Math.round(w * CARD_RATIO);
  const boxH = !card && !note && emptyHeight > h ? emptyHeight : h;
  return (
    <div data-testid="card-detail-slot" className="relative shrink-0" style={{ width: w, height: boxH }}>
      {!card && note ? (
        <div
          key="note"
          data-testid="large-card-slot"
          data-empty="false"
          data-source="board"
          className="guidance-fade absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-6"
          style={{ borderRadius: "var(--radius-card)", background: "var(--deep-water)", color: "#FBFAF7" }}
        >
          <span data-testid="large-card-note" style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>{note.title}</span>
          {note.sub && <span style={{ fontSize: 12.5, opacity: 0.85 }}>{note.sub}</span>}
        </div>
      ) : !card ? (
        <div
          key="empty"
          data-testid="large-card-slot"
          data-empty="true"
          className="guidance-fade absolute inset-0 flex items-center justify-center text-center px-6"
          style={{ borderRadius: "var(--radius-card)", border: "1.5px dashed #B9B3A6", color: "var(--ink-5)", fontSize: 13 }}
        >
          Select a card to read it
        </div>
      ) : (
        <button
          key={card.id}
          type="button"
          data-testid="large-card-slot"
          data-empty="false"
          data-card-key={card.key}
          data-source={source}
          onClick={onOpen}
          aria-label={`Open full-size view: ${card.name}`}
          className="guidance-fade absolute inset-0 block p-0 border-0 bg-transparent cursor-zoom-in"
        >
          <img decoding="async"
            src={cardImageSrc(card.key)}
            alt={cardAlt(card)}
            data-testid="large-card-img"
            draggable={false}
            className="block select-none"
            style={{
              width: w,
              height: h,
              objectFit: "contain",
              borderRadius: "var(--radius-card)",
              boxShadow: "var(--shadow-card)",
              ...(isLegacyArt(card.key) ? { background: "var(--card)" } : {}),
            }}
          />
          {detailLines.length > 0 && (
            <span
              data-testid="large-card-detail"
              className="absolute flex flex-col justify-center text-left"
              style={{
                left: "5.5%",
                right: "5.5%",
                bottom: "3.2%",
                minHeight: "9.6%",
                padding: "5px 9px",
                gap: 3,
                borderRadius: 10,
                background: "var(--deep-water)",
                color: "#FBFAF7",
                fontSize: Math.max(11, Math.round(w * 0.047)),
                fontWeight: 700,
                lineHeight: 1.25,
              }}
            >
              {detailLines.map((line, i) => (
                <span key={i} data-testid={/^Resilience /.test(line) ? "large-card-resilience" : /^Height /.test(line) ? "large-card-height" : `large-card-detail-line-${i}`}>
                  {line}
                </span>
              ))}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
